import { Question } from "survey-core";

/**
 * Model for the schema's `datepicker` question type. Deliberately the same
 * minimal shape as the shared package's `DatePickerQuestionModel`: a plain
 * `Question` whose value is an ISO date string. The admin-v2 renderer lands
 * in chunk 3.2; declaring the model here is what lets the seeded schema parse.
 */
export class DatePickerQuestionModel extends Question {
  getType(): string {
    return "datepicker";
  }
}

/**
 * Model for the schema's `licensePlate` question type. A plain `Question`,
 * mirroring the shared package's `LicensePlateQuestionModel`: the seeded
 * schema gives this question no `choices` — the value is a free-text plate,
 * enriched by the RDW lookup in chunk 3.2 (`LicensePlateInput` /
 * `useLicensePlateLookup`). It inherits `payloadPath` from the property
 * registered on the base `question` class (SurveyJS metadata merges parent
 * properties recursively and resets descendant caches on `addProperty`).
 *
 * Only the properties the seeded schema actually uses are declared — the
 * shared serializer's `fleetCheck` / `evaluationGroupId` belong to the
 * widget's evaluation-engine flow and are not needed here.
 */
export class LicensePlateQuestionModel extends Question {
  getType(): string {
    return "licensePlate";
  }

  /** ISO country code of the plate format; only NL plates exist today. */
  get country(): string {
    return this.getPropertyValue("country");
  }
  set country(value: string) {
    this.setPropertyValue("country", value);
  }

  /**
   * Where the value lands in the create-dossier payload. Re-declared on the
   * class although it is also registered on the base `question` class. A
   * standalone probe against the installed survey-core confirms why: with
   * `payloadPath` registered only on `"question"`, loading
   * `{type: "licensePlate", payloadPath: "..."}` through `ElementFactory`
   * inherits the *metadata* (`Serializer` finds the property) but never
   * materialises the per-instance accessor — `getPropertyValue` returns
   * undefined while the JSON value sits on the instance as a plain own data
   * property (`obj.payloadPath` reads fine) that shadows the accessor. The
   * class-local registration (see registerSurveyExtensions) makes the custom
   * type's own metadata own the property so the accessor exists.
   */
  get payloadPath(): string | undefined {
    return this.getPropertyValue("payloadPath");
  }
  set payloadPath(value: string | undefined) {
    this.setPropertyValue("payloadPath", value);
  }
}
